import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { CookieBanner } from '@/components/common/CookieBanner';
import { AnalyticsTracker } from '@/components/common/AnalyticsTracker';
import { AuthProvider } from '@/context/AuthContext';
import { EmployeeAuthProvider } from '@/context/EmployeeAuthContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import EmployeeProtectedRoute from '@/components/auth/EmployeeProtectedRoute';
// Eager: the public acquisition path (homepage LCP + login/signup) must
// render without waiting for a second network round-trip.
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import '@/App.css';

// Lazy: everything behind auth (and rarely-hit public pages) is split into
// per-route chunks so marketing/ads visitors don't download the dashboard,
// portal, kiosk, payroll or super-admin code. (Vrinsoft audit: "unused
// JavaScript" / "excessive JavaScript execution".)
const GoogleCallbackPage = lazy(() => import('@/pages/GoogleCallbackPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const KioskPage = lazy(() => import('@/pages/KioskPage'));
const SuperAdminPage = lazy(() => import('@/pages/SuperAdminPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const EmployeesPage = lazy(() => import('@/pages/EmployeesPage'));
const SchedulePage = lazy(() => import('@/pages/SchedulePage'));
const AttendancePage = lazy(() => import('@/pages/AttendancePage'));
const InsightsPage = lazy(() => import('@/pages/InsightsPage'));
const LeavePage = lazy(() => import('@/pages/LeavePage'));
const PayrollPage = lazy(() => import('@/pages/PayrollPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const BillingSuccessPage = lazy(() => import('@/pages/BillingSuccessPage'));
const BillingCancelPage = lazy(() => import('@/pages/BillingCancelPage'));
const PortalLoginPage = lazy(() => import('@/pages/PortalLoginPage'));
const PortalDashboardPage = lazy(() => import('@/pages/PortalDashboardPage'));
const PortalSchedulePage = lazy(() => import('@/pages/PortalSchedulePage'));
const PortalAttendancePage = lazy(() => import('@/pages/PortalAttendancePage'));
const PortalLeavePage = lazy(() => import('@/pages/PortalLeavePage'));
const PortalPayslipsPage = lazy(() => import('@/pages/PortalPayslipsPage'));
const PortalResourcesPage = lazy(() => import('@/pages/PortalResourcesPage'));
const PortalPrivacyPage = lazy(() => import('@/pages/PortalPrivacyPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const DpaPage = lazy(() => import('@/pages/legal/DpaPage'));
const CookiePolicyPage = lazy(() => import('@/pages/legal/CookiePolicyPage'));

/** Minimal, brand-consistent loading state while a route chunk downloads. */
function RouteFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[color:var(--brand-bg)]"
      data-testid="route-loading-fallback"
      role="status"
      aria-label="Loading"
    >
      <div className="h-8 w-8 rounded-full border-2 border-[color:var(--brand-border)] border-t-[color:var(--brand-primary)] animate-spin" />
    </div>
  );
}

/**
 * Homepage: the marketing landing page for logged-out visitors (rotahq.uk
 * ad + SEO traffic); logged-in users go straight to their dashboard.
 */
function HomeRoute() {
  try {
    const raw = localStorage.getItem('rota_user');
    if (raw) {
      const user = JSON.parse(raw);
      return <Navigate to={user.role === 'super_admin' ? '/super-admin' : '/dashboard'} replace />;
    }
  } catch {
    /* fall through to the landing page */
  }
  return <LandingPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <EmployeeAuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/google" element={<GoogleCallbackPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/dpa" element={<DpaPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/kiosk" element={<KioskPage />} />
              <Route path="/kiosk/:slug" element={<KioskPage />} />

              <Route path="/portal/login" element={<PortalLoginPage />} />
              <Route element={<EmployeeProtectedRoute />}>
                <Route path="/portal" element={<PortalDashboardPage />} />
                <Route path="/portal/schedule" element={<PortalSchedulePage />} />
                <Route path="/portal/attendance" element={<PortalAttendancePage />} />
                <Route path="/portal/leave" element={<PortalLeavePage />} />
                <Route path="/portal/payslips" element={<PortalPayslipsPage />} />
                <Route path="/portal/resources" element={<PortalResourcesPage />} />
                <Route path="/portal/privacy" element={<PortalPrivacyPage />} />
              </Route>

              <Route element={<ProtectedRoute roles={['super_admin']} />}>
                <Route path="/super-admin" element={<SuperAdminPage tab="tenants" />} />
                <Route path="/super-admin/signups" element={<SuperAdminPage tab="signups" />} />
                <Route path="/super-admin/metrics" element={<SuperAdminPage tab="metrics" />} />
                <Route path="/super-admin/demos" element={<SuperAdminPage tab="demos" />} />
                <Route path="/super-admin/audit" element={<SuperAdminPage tab="audit" />} />
              </Route>

              <Route element={<ProtectedRoute roles={['admin']} />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/attendance" element={<AttendancePage />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/leave-requests" element={<LeavePage />} />
                <Route path="/payroll" element={<PayrollPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/upgrade" element={<BillingPage />} />
                <Route path="/billing/success" element={<BillingSuccessPage />} />
                <Route path="/billing/cancel" element={<BillingCancelPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
          <Toaster richColors position="top-right" closeButton />
          <CookieBanner />
        </BrowserRouter>
      </EmployeeAuthProvider>
    </AuthProvider>
  );
}
